import { parseItAssetLocation } from './itAssetLocation'

export type ComputerLabDef = {
  id: string
  label: string
  /** Jméno a příjmení učitele učebny (dle evidence zaměstnanců). */
  teacher: string
  building: string
  /** Kód místnosti pro složená umístění (např. „6-101c / M119 / M111“). */
  roomKey?: string
  matchesRoom: (room: string) => boolean
}

/** Soubor 009768 — děti mají vlastní i.č. a umístění z PDF přílohy. */
const SKUPINA_009768 = '009768'

type LabItem = {
  inventory_number?: string
  category?: string
  parent_inventory?: string | null
  location?: string | null
  monitor_location?: string | null
}

function roomStartsWithKey(room: string, key: string): boolean {
  const trimmed = room.trim()
  if (!trimmed) return false
  const firstToken = trimmed.split(/\s+/)[0]?.split('/')[0] ?? ''
  return firstToken.toUpperCase().startsWith(key.toUpperCase())
}

function roomContainsKey(room: string, key: string): boolean {
  return room.trim().toUpperCase().includes(key.toUpperCase())
}

function roomTokenInLocation(location: string | null | undefined, roomKey: string): boolean {
  const raw = (location || '').trim()
  if (!raw) return false
  const token = roomKey.toUpperCase()
  if (new RegExp(`(?:^|[\\s/])${token}\\b`, 'i').test(raw)) return true
  const parsed = parseItAssetLocation(location)
  return roomStartsWithKey(parsed.room, roomKey)
}

/** Počítačové učebny — mapování podle budovy a kódu místnosti. */
export const COMPUTER_LABS: ComputerLabDef[] = [
  {
    id: 'k29-k101b',
    label: 'K-29 · K101b',
    teacher: 'Pavel Rochel',
    building: 'K-29',
    roomKey: 'K101b',
    matchesRoom: (room) => roomStartsWithKey(room, 'K101b'),
  },
  {
    id: 'k29-k204',
    label: 'K-29 · K204',
    teacher: 'Jiří Mondek',
    building: 'K-29',
    roomKey: 'K204',
    matchesRoom: (room) => roomStartsWithKey(room, 'K204'),
  },
  {
    id: 'k29-k209',
    label: 'K-29 · K209',
    teacher: 'Michal Štádler',
    building: 'K-29',
    roomKey: 'K209',
    matchesRoom: (room) => roomStartsWithKey(room, 'K209'),
  },
  {
    id: 'm68-m119',
    label: 'M-68 · M119',
    teacher: 'Jiří Král',
    building: 'M-68',
    roomKey: 'M119',
    matchesRoom: (room) => roomStartsWithKey(room, 'M119'),
  },
  {
    id: 'm68-m111',
    label: 'M-68 · M111',
    teacher: 'Zdeněk Vrbata',
    building: 'M-68',
    roomKey: 'M111',
    matchesRoom: (room) => roomStartsWithKey(room, 'M111'),
  },
  {
    id: 'm68-m227',
    label: 'M-68 · M227',
    teacher: 'Zdeněk Perlinger',
    building: 'M-68',
    roomKey: 'M227',
    matchesRoom: (room) => roomStartsWithKey(room, 'M227'),
  },
  {
    id: 'nh1-a303',
    label: 'NH-1 · A303',
    teacher: 'Pavol Procházka',
    building: 'NH-1',
    roomKey: 'A303',
    matchesRoom: (room) => roomStartsWithKey(room, 'A303'),
  },
  {
    id: 'nh1-a102',
    label: 'NH-1 · A102',
    teacher: 'Miroslav Krákora',
    building: 'NH-1',
    roomKey: 'A102',
    matchesRoom: (room) => roomStartsWithKey(room, 'A102'),
  },
  {
    id: 'd8-d466',
    label: 'D-8 · D466',
    teacher: 'Serhii Vasyliev',
    building: 'D-8',
    roomKey: 'D466',
    matchesRoom: (room) => roomContainsKey(room, 'D466'),
  },
]

function locationMatchesLab(
  location: string | null | undefined,
  lab: ComputerLabDef,
): boolean {
  const parsed = parseItAssetLocation(location)
  if (parsed.building === lab.building && lab.matchesRoom(parsed.room)) return true
  if (lab.roomKey && roomTokenInLocation(location, lab.roomKey)) return true
  if (lab.id === 'd8-d466' && location && roomContainsKey(location, 'D466')) return true
  return false
}

export function itemMatchesComputerLab(item: LabItem, labId: string): boolean {
  const lab = COMPUTER_LABS.find((entry) => entry.id === labId)
  if (!lab) return false

  // Děti souboru 009768 — podle umístění PC nebo monitoru z PDF přílohy
  if (item.parent_inventory === SKUPINA_009768) {
    if (locationMatchesLab(item.location, lab)) return true
    if (item.monitor_location && locationMatchesLab(item.monitor_location, lab)) return true
  }

  if (locationMatchesLab(item.location, lab)) return true
  if (item.monitor_location && locationMatchesLab(item.monitor_location, lab)) return true
  return false
}

export function itemMatchesAnyComputerLab(
  item: LabItem,
  labIds: ReadonlySet<string>,
): boolean {
  if (labIds.size === 0) return true
  for (const labId of labIds) {
    if (itemMatchesComputerLab(item, labId)) return true
  }
  return false
}
